import type { TokenStore } from "@/domains/tokens/token-store"
import {
  positionFromAmount0,
  positionFromAmount1,
} from "@/domains/manual-cl-core"

interface CommonParams {
  /**
   * Current pool price as sqrtPriceX96 from `slot0()`. Preferred over
   * `getSqrtRatioAtTick(currentTick)` — when slot0 is available it encodes
   * the exact sub-tick price, avoiding rounding error that biases the
   * dependent-amount calculation in one direction.
   */
  sqrtPriceX96Current: bigint
  tickLower: number
  tickUpper: number
  token0: TokenStore
  token1: TokenStore
  fee: number
}

/**
 * Given an amount of token0 the user wants to deposit, derive the companion
 * amount of token1 that must be supplied to form a position with matching
 * liquidity. Bit-exact with Uniswap v3 via manual-cl-core math wrappers.
 *
 * Correctly handles single-sided (out-of-range) positions: if the position
 * is fully above range, `amount1` will be non-zero while `amount0` input is
 * ignored by Uniswap's SDK; we mirror that here by returning the computed
 * amount1 pair.
 */
export function calculateDependentAmount1FromAmount0(
  params: CommonParams & { amount0: bigint },
): bigint {
  if (params.amount0 <= 0n) return 0n
  const { amount1 } = positionFromAmount0({
    sqrtPriceX96: params.sqrtPriceX96Current,
    tickLower: params.tickLower,
    tickUpper: params.tickUpper,
    amount0: params.amount0,
    token0: params.token0,
    token1: params.token1,
    fee: params.fee,
  })
  return amount1
}

/**
 * Inverse of `calculateDependentAmount1FromAmount0`.
 */
export function calculateDependentAmount0FromAmount1(
  params: CommonParams & { amount1: bigint },
): bigint {
  if (params.amount1 <= 0n) return 0n
  const { amount0 } = positionFromAmount1({
    sqrtPriceX96: params.sqrtPriceX96Current,
    tickLower: params.tickLower,
    tickUpper: params.tickUpper,
    amount1: params.amount1,
    token0: params.token0,
    token1: params.token1,
    fee: params.fee,
  })
  return amount0
}
